import { SimpleGrid } from '@chakra-ui/react';
import {
  LegalLayout,
  Heading,
  Text,
  OrderedList,
  UnorderedList,
  ListItem,
  Link,
  Box,
} from '../components/LegalLayout';

const HOTLINES: { state: string; phone: string }[] = [
  { state: 'Alabama', phone: '1-800-GAMBLER' },
  { state: 'Alaska', phone: '1-800-GAMBLER' },
  { state: 'Arizona', phone: '1-800-NEXT-STEP' },
  { state: 'Arkansas', phone: '1-800-522-4700' },
  { state: 'California', phone: '1-800-GAMBLER' },
  { state: 'Colorado', phone: '1-800-522-4700' },
  { state: 'Connecticut', phone: '1-888-789-7777' },
  { state: 'Delaware', phone: '1-888-850-8888' },
  { state: 'Florida', phone: '1-888-236-4848' },
  { state: 'Georgia', phone: '1-800-GAMBLER' },
  { state: 'Hawaii', phone: '1-800-GAMBLER' },
  { state: 'Idaho', phone: '1-800-GAMBLER' },
  { state: 'Illinois', phone: '1-800-GAMBLER' },
  { state: 'Indiana', phone: '1-800-GAMBLER' },
  { state: 'Iowa', phone: '1-800-BETS-OFF' },
  { state: 'Kansas', phone: '1-800-522-4700' },
  { state: 'Kentucky', phone: '1-800-GAMBLER' },
  { state: 'Louisiana', phone: '1-877-770-STOP' },
  { state: 'Maine', phone: '1-800-GAMBLER' },
  { state: 'Maryland', phone: '1-800-GAMBLER' },
  { state: 'Massachusetts', phone: '1-800-327-5050' },
  { state: 'Michigan', phone: '1-800-GAMBLER' },
  { state: 'Minnesota', phone: '1-800-833-4673' },
  { state: 'Mississippi', phone: '1-888-777-9696' },
  { state: 'Missouri', phone: '1-800-GAMBLER' },
  { state: 'Montana', phone: '1-888-900-9979' },
  { state: 'Nebraska', phone: '1-833-238-6837' },
  { state: 'Nevada', phone: '1-800-522-4700' },
  { state: 'New Hampshire', phone: '1-603-724-1605' },
  { state: 'New Jersey', phone: '1-800-GAMBLER' },
  { state: 'New Mexico', phone: '1-800-GAMBLER' },
  { state: 'New York', phone: '1-877-846-7369' },
  { state: 'North Carolina', phone: '1-800-522-4700' },
  { state: 'North Dakota', phone: '1-800-GAMBLER' },
  { state: 'Ohio', phone: '1-800-589-9966' },
  { state: 'Oklahoma', phone: '1-800-GAMBLER' },
  { state: 'Oregon', phone: '1-877-695-4648' },
  { state: 'Pennsylvania', phone: '1-800-GAMBLER' },
  { state: 'Puerto Rico', phone: '1-800-981-0023' },
  { state: 'Rhode Island', phone: '1-877-942-6253' },
  { state: 'South Carolina', phone: '1-800-GAMBLER' },
  { state: 'South Dakota', phone: '1-800-GAMBLER' },
  { state: 'Tennessee', phone: '1-800-889-9789' },
  { state: 'Texas', phone: '1-800-GAMBLER' },
  { state: 'Utah', phone: '1-800-GAMBLER' },
  { state: 'Vermont', phone: '1-800-GAMBLER' },
  { state: 'Virginia', phone: '1-800-GAMBLER' },
  { state: 'Washington DC', phone: '1-800-522-4700' },
  { state: 'Washington', phone: '1-800-547-6133' },
  { state: 'West Virginia', phone: '1-800-GAMBLER' },
  { state: 'Wisconsin', phone: '1-800-GAMBLER' },
  { state: 'Wyoming', phone: '1-800-426-2535' },
];

function telHref(phone: string): string {
  // Keep digits and vanity letters so tel:1-800-GAMBLER still works.
  return `tel:${phone.replace(/[^\dA-Za-z+]/g, '')}`;
}

export default function ResponsibleGaming() {
  return (
    <LegalLayout
      title="Responsible Gaming"
      description="WagerWatchdog responsible gaming resources, responsible play tips, and gambling hotlines by state."
      lastUpdated="July 11, 2026"
    >
      <Heading as="h2">Our Commitment</Heading>
      <Text>
        WagerWatchdog is a sports betting analytics platform. We help users
        evaluate expert performance with verified pick outcomes and transparent
        scoring. We do not accept wagers, facilitate bets, or operate as a
        sportsbook. The platform is meant for entertainment and analytic
        purposes only, and is intended for adult use only (21+ where required by
        law; otherwise 18+).
      </Text>
      <Text>
        We are committed to promoting responsible engagement with sports betting
        information. If you or someone you know has a gambling problem and wants
        help, call{' '}
        <Link href="tel:1800GAMBLER">1-800-GAMBLER</Link> or use the
        state-specific resources below.
      </Text>

      <Heading as="h2">Get Help</Heading>
      <UnorderedList>
        <ListItem>
          National helpline:{' '}
          <Link href="tel:1800GAMBLER">1-800-GAMBLER</Link> (1-800-426-2537)
        </ListItem>
        <ListItem>
          National Council on Problem Gambling:{' '}
          <Link href="https://www.ncpgambling.org/" isExternal>
            ncpgambling.org
          </Link>
        </ListItem>
        <ListItem>
          Text support (where available): text &ldquo;HELLO&rdquo; to 467369
        </ListItem>
      </UnorderedList>

      <Heading as="h2">Responsible Play Tips</Heading>
      <Text>
        Sports betting and related entertainment are enjoyable for most people
        when done responsibly. Responsible play means knowing your limits,
        staying within your means, and stopping when you choose. Build healthy
        habits with the practices below:
      </Text>
      <OrderedList>
        <ListItem>
          Treat betting and betting research as entertainment, not as a way to
          make money.
        </ListItem>
        <ListItem>
          Set a spending cap for any wagering activity and stick to it.
        </ListItem>
        <ListItem>
          Make sure you understand the bet, market, or product before you
          engage.
        </ListItem>
        <ListItem>
          Always use your own funds&mdash;never money you cannot afford to lose
          or money you have borrowed.
        </ListItem>
        <ListItem>
          Keep balance by spending time on other hobbies and activities.
        </ListItem>
        <ListItem>
          Do not bet or make betting decisions when you have been drinking or
          are not fully sober.
        </ListItem>
        <ListItem>
          Never chase losses. Do not try to win back what you have lost.
        </ListItem>
        <ListItem>
          Skip wagering if you are feeling down, anxious, or highly stressed.
        </ListItem>
        <ListItem>
          Set a time limit for how long you engage and follow it.
        </ListItem>
        <ListItem>
          Learn the signs of at-risk or problem gambling and seek help early if
          they apply to you or someone you care about.
        </ListItem>
      </OrderedList>

      <Heading as="h2">Gambling Hotlines by State</Heading>
      <Text>
        If you need localized support, contact the helpline for your state or
        territory:
      </Text>
      <SimpleGrid columns={{ base: 1, sm: 2 }} spacingX={6} spacingY={2} mb={4}>
        {HOTLINES.map(({ state, phone }) => (
          <Box key={state} as="p" mb={0}>
            <Text as="span" fontWeight="semibold" color="white">
              {state}
            </Text>
            {': '}
            <Link href={telHref(phone)}>{phone}</Link>
          </Box>
        ))}
      </SimpleGrid>

      <Heading as="h2">Disclaimer</Heading>
      <Text>
        This platform is meant for entertainment and analytic purposes only. If
        you or someone you know has a gambling problem and wants help, call{' '}
        <Link href="tel:1800GAMBLER">1-800 GAMBLER</Link>. WagerWatchdog is
        intended for adult use only.
      </Text>
    </LegalLayout>
  );
}
